import axios from "axios";
import {Category} from "@/utils/Category";
import {Product} from "~/utils/Product";
import {User} from "~/utils/User";
import {Cart, cartToJson} from "~/utils/Cart";
import {useUserDataStorage, type RequestData} from "~/store/userdata";
import {Article} from "~/utils/Article";
import {Case} from "~/utils/Case";
import {BlogArticle} from "~/utils/BlogArticle";
import {BlogCategory} from "~/utils/BlogCategory";
import {EventCategory} from "~/utils/EventCategory";
import { useMyFunctions } from "./MyFunctions";

export function useModyApi() {
  const userDataStore = useUserDataStorage();
  const myFunctions = useMyFunctions();
  const { user } = storeToRefs(userDataStore)
  const baseUrl = useRuntimeConfig().public.apiUrl
  const api = axios.create({
    baseURL: baseUrl,
    headers: user.value?.token ? { Authorization: `Bearer ${user.value?.token}` } : {}
  });

  async function get(url:string){
    return await api.get(url);
  }
  async function post(url:string,data:any){
    return await api.post(url,data);
  }
  async function update(url:string, data:any){
    try {
      return await api.put(url,{
        data: data
      });
    }
    catch (e) {
      console.log(e)
      return e
    }
  }
  async function put(url:string, data:any){
    try {
      return await api.put(url,
        data
    );
    }
    catch (e) {
      console.log(e)
      return e
    }
  }
  async function create(url:string, data:any){
    try {
      return await api.post(url,{
        data: data
      });
    }
    catch (e) {
      throw e
    }
  }
  async function remove(url:string){
    return await api.delete(url);
  }

  function formatRequestDate(date: Date): string {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  function searchTokens(searchString: string | null | undefined): string[] {
    const trimmed = String(searchString ?? "").trim()
    if (!trimmed) {
      return []
    }

    const seen = new Set<string>()
    return trimmed
      .split(/\s+/)
      .map((token) => token.trim())
      .filter((token) => token.length > 0)
      .filter((token) => {
        const normalized = token.toLowerCase()
        if (seen.has(normalized)) {
          return false
        }
        seen.add(normalized)
        return true
      })
  }

  function appendContainsAllTokens(params: URLSearchParams, fieldPath: string, searchString: string, caseInsensitive = false) {
    const operation = caseInsensitive ? "$containsi" : "$contains"
    searchTokens(searchString).forEach((token, index) => {
      params.append(`filters[$and][${index}][${fieldPath}][${operation}]`, token)
    })
  }

  async function downloadEventDocument(eventId:number, type:"offer"|"invoice"){
    return await api.post(
      `events/${eventId}/generate-document`,
      { type },
      {
        responseType: "blob",
      }
    );
  }

  async function generateEventCableList(eventId:number){
    return await api.post(`events/${eventId}/generate-cable-list`, {});
  }

  async function sendFeedbackMail(eventId:number){
    return await api.post(`events/${eventId}/send-feedback-email`, {});
  }

  async function markFeedbackGiven(eventId:number){
    return await api.post(`events/${eventId}/mark-feedback-given`, {});
  }

  async function assignArticleToEvent(articleId:number, eventId:number){
    return await api.post(`articles/${articleId}/assign-event`, { eventId });
  }

  async function clearArticleEvent(articleId:number, eventId?:number){
    return await api.post(`articles/${articleId}/clear-event`, eventId ? { eventId } : {});
  }

  async function assignArticleInternalUse(articleId:number){
    return await api.post(`articles/${articleId}/assign-internal-use`, {});
  }

  async function clearArticleInternalUse(articleId:number){
    return await api.post(`articles/${articleId}/clear-internal-use`, {});
  }

  async function markArticleSold(articleId:number){
    return await api.post(`articles/${articleId}/mark-sold`, {});
  }

  async function markArticleInStock(articleId:number){
    return await api.post(`articles/${articleId}/mark-in-stock`, {});
  }


  async function getCasesPaginated(page:number, pageSize:number, filterString:string) {
    page++
    const params = new URLSearchParams()
    params.set("populate", "targetContents.product,articles,articles.product,articles.activeEvent,articles.activeService,extraContents.product,openInternalContents.product,activeEvent")
    params.set("pagination[page]", String(page))
    params.set("pagination[pageSize]", String(pageSize))
    searchTokens(filterString).forEach((token, index) => {
      params.append(`filters[$and][${index}][$or][0][name][$containsi]`, token)
      params.append(`filters[$and][${index}][$or][1][barcode][$containsi]`, token)
    })
    const response = await api.get("cases?" + params.toString())
    const totalRecords = response.data.meta.pagination.total
    const cases:Case[] = response.data.data.map((value:any) => {
      const tempCase = new Case()
      tempCase.parseJson(value)
      return tempCase
    })
    return {cases, totalRecords}
  }

  async function getCase(caseId:number): Promise<Case> {
    const tempCase = new Case()
    const response = await api.get(`cases/${caseId}?populate=targetContents.product,articles,articles.product,articles.activeEvent,articles.activeService,extraContents.product,openInternalContents.product,activeEvent`)
    tempCase.parseJson(response.data.data)
    return tempCase
  }

  async function searchCases(searchString:string): Promise<Case[]> {
    const params = new URLSearchParams()
    params.set("populate", "targetContents.product,articles,articles.product,extraContents.product,openInternalContents.product,activeEvent")
    searchTokens(searchString).forEach((token, index) => {
      params.append(`filters[$and][${index}][$or][0][name][$containsi]`, token)
      params.append(`filters[$and][${index}][$or][1][barcode][$containsi]`, token)
    })
    const response = await api.get("cases?" + params.toString())
    return response.data.data.map((value:any) => {
      const tempCase = new Case()
      tempCase.parseJson(value)
      return tempCase
    })
  }

  async function searchArticles(searchString:string): Promise<Article[]> {
    const params = new URLSearchParams()
    params.set("populate", "product,activeEvent,activeService,currentCase")
    searchTokens(searchString).forEach((token, index) => {
      params.append(`filters[$and][${index}][$or][0][article_number][$containsi]`, token)
      params.append(`filters[$and][${index}][$or][1][product][Name][$containsi]`, token)
    })
    params.set("pagination[pageSize]", "25")
    const response = await api.get("articles?" + params.toString())
    return response.data.data.map((value:any) => {
      const tempArticle = new Article()
      tempArticle.parseJson(value)
      return tempArticle
    })
  }

  async function assignArticleToCase(caseId:number, articleId:number){
    return await api.post(`cases/${caseId}/assign-article`, { articleId })
  }

  async function removeArticleFromCase(caseId:number, articleId:number){
    return await api.post(`cases/${caseId}/remove-article`, { articleId })
  }

  async function assignCaseToEvent(caseId:number, eventId:number){
    return await api.post(`cases/${caseId}/assign-event`, { eventId })
  }

  async function clearCaseEvent(caseId:number, eventId?:number){
    return await api.post(`cases/${caseId}/clear-event`, eventId ? { eventId } : {})
  }

  async function assignCaseInternalUse(caseId:number){
    return await api.post(`cases/${caseId}/assign-internal-use`, {})
  }

  async function clearCaseInternalUse(caseId:number){
    return await api.post(`cases/${caseId}/clear-internal-use`, {})
  }

  async function getProductsPaginated(page:number, pageSize:number,filterString:string) {
    page++
    const params = new URLSearchParams()
    params.set("populate", "Images,categories,articles")
    params.set("pagination[page]", String(page))
    params.set("pagination[pageSize]", String(pageSize))
    appendContainsAllTokens(params, "Name", filterString)
    const response = await api.get("products?" + params.toString());
    const totalRecords = response.data.meta.pagination.total
    const products:Product[] = response.data.data.map((value:any) => {
      const tempProduct = new Product();
      tempProduct.parseJson(value);
      return tempProduct;
    });

    return {products, totalRecords}
  }

  async function getArticlesPaginated(page:number, pageSize:number,filterString:string) {
    page++
    const params = new URLSearchParams()
    params.set("populate", "product")
    params.set("pagination[page]", String(page))
    params.set("pagination[pageSize]", String(pageSize))
    searchTokens(filterString).forEach((token, index) => {
      params.append(`filters[$and][${index}][$or][0][article_number][$contains]`, token)
      params.append(`filters[$and][${index}][$or][1][product][Name][$contains]`, token)
    })
    const response = await api.get("articles?" + params.toString());
    const totalRecords = response.data.meta.pagination.total
    const articles:Article[] = response.data.data.map((value:any) => {
      const tempArticle = new Article()
      tempArticle.parseJson(value)
      return tempArticle;
    });

    return {articles, totalRecords}
  }


  async function getCategoryBySlug(slug:string):Promise<Category>{
    const category:Category = new Category()
    const response = await api.get(
        "categories?filters[slug][$eq]="+slug+"&populate=parent,categories"
    );
    if(response.data.data.length > 0){
      category.parseJson(response.data.data[0]);
    }
    return category
  }

  async function getCategory(id:Number):Promise<Category>{
    const category:Category = new Category()
    const response = await api.get(
        "categories/"+id+
        "?populate=parent,categories"
    );
    category.parseJson(response.data.data)
    return category
  }
  async function getCategories() {
    const response = await api.get(
        "categories?pagination[pageSize]=100&populate=categories,parent,products,categories.products"
    );
    const responseCats = response.data.data as {
      id: number;
      attributes: { [key: string]: any };
    }[];
    let categories: Category[] = [];
    responseCats.forEach((value) => {
      if (!value.attributes.parent.data) {
        const tempCategory = new Category();
        tempCategory.parseJson(value);
        categories.push(tempCategory);
      }
    });

    return categories;
  }

  async function getAllCategories(){
    const response = await api.get(
      "categories?pagination[pageSize]=100&populate=parent,products&sort=Name:asc"
    );
    const responseCats = response.data.data as {
      id: number;
      attributes: { [key: string]: any };
    }[];
    let categories: Category[] = [];
    responseCats.forEach((value) => {
        const tempCategory = new Category();
        tempCategory.parseJson(value);
        categories.push(tempCategory);
    });

    return categories;
  }

  async function getEventCategories(includeInactive = true): Promise<EventCategory[]> {
    const params = new URLSearchParams()
    params.set("pagination[pageSize]", "100")
    params.set("sort[0]", "sortOrder:asc")
    params.set("sort[1]", "Name:asc")
    if (!includeInactive) {
      params.set("filters[active][$eq]", "true")
    }

    const response = await api.get("event-categories?" + params.toString())
    return response.data.data.map((value: any) => {
      const category = new EventCategory()
      category.parseJson(value)
      return category
    })
  }
  
  async function getCategoryProducts(categorySlug: string): Promise<Product[]> {
    // Get Products of Main category
    const response = await api.get(
        "products" +
        "?filters[categories][slug][$in]="+categorySlug+"&populate=childProducts.product.Images,Images,categories.categories"
    );
    let mainCategoryProducts = response.data.data.map((value:any) => {
      const tempProduct = new Product();
      tempProduct.parseJson(value);
      return tempProduct;
    });
    const category = await getCategoryBySlug(categorySlug);
    if (category && category.subCategories.length > 0) {
      // Fetch Products of Subcategories in parallel
      const subCategoriesPromises = category.subCategories.map(subCategory =>
          getCategoryProducts(subCategory.slug)
      );
      const subCategoriesProducts = await Promise.all(subCategoriesPromises);

      // Flatten and concatenate subcategories products
      const subCategoriesProductsFlat = subCategoriesProducts.flat();
      // Combine main category products with subcategory products
      
      mainCategoryProducts =  mainCategoryProducts.concat(subCategoriesProductsFlat);
    }
    // Remove duplicate products
    mainCategoryProducts = myFunctions.removeDuplicateProducts(mainCategoryProducts);
    return mainCategoryProducts;
  }

  async function getProductBySlug(slug: string): Promise<Product> {
    const tempProduct: Product = new Product()

    // Basis-Query für das Hauptprodukt
    const url =
      "products?filters[slug][$eq]=" +
      slug +
      "&populate=childProducts.product.Images,Images,categories,articles,related_products.Images,categories"

    try {
      const response = await api.get(url)
      if (response.data.data.length > 0) {
        tempProduct.parseJson(response.data.data[0])
      }

      // Aktuelle Kategorie-ID (erste Kategorie)
      const catId = tempProduct.categories?.[0]?.id
      const slugFilter = slug

      // vorhandene Relateds (manuell gepflegt)
      const related = tempProduct.related_products
      const relatedSlugs = related.map((p: Product) => p.slug)
      // Falls weniger als 4 vorhanden, automatisch auffüllen
      if (catId && related.length < 4) {
        const limit = 4 - related.length
        const fallbackUrl =
          `products?filters[categories][id][$eq]=${catId}` +
          `&filters[slug][$ne]=${slugFilter}` + // aktuelles Produkt ausschließen
          (relatedSlugs.length
            ? `&filters[slug][$ne]=[${relatedSlugs.join(',')}]`
            : '') + // schon vorhandene nicht nochmal laden
          `&pagination[limit]=${limit}&populate=Images,categories`

        const fallback = await api.get(fallbackUrl)
        const fallbackProducts = fallback.data.data.map((p: any) => {
          const prod = new Product()
          prod.parseJson(p)
          return prod
        })

        // kombinieren: manuelle zuerst, dann automatische Ergänzung
        tempProduct.related_products = [...related, ...fallbackProducts]
      }
    } catch (e) {
      throw e
    }

    return tempProduct
  }

  async function getProduct(productId: number): Promise<Product> {
    const tempProduct: Product = new Product();
    try {
      const response = await api.get("products/" + productId + "?populate=childProducts.product.Images,Images,categories,articles");
      tempProduct.parseJson(response.data.data);
    } catch (e) {
      throw(e)
    }
    return tempProduct;
  }
  async function searchProducts(searchString: string): Promise<Product[]> {
    const tempProducts: Product[] = []
    const query = String(searchString ?? "").trim().replace(/\s+/g, " ")
    const tokens = searchTokens(query)
    if (query.length < 3 || tokens.length === 0) {
      return tempProducts
    }

    const params = new URLSearchParams()
    params.set("populate", "Images,categories")
    params.set("pagination[pageSize]", "60")
    tokens.forEach((token, index) => {
      params.append(`filters[$and][${index}][$or][0][Name][$containsi]`, token)
      params.append(`filters[$and][${index}][$or][1][Description][$containsi]`, token)
    })
    if (!userDataStore.loggedIn) {
      params.set("filters[Public][$eq]", "true")
    }
    const response = await api.get("products?" + params.toString());
    response.data.data.forEach((value:any) => {
      const tempProduct = new Product();
      tempProduct.parseJson(value);
      tempProducts.push(tempProduct);
    });

    const normalize = (value: string) => value.toLocaleLowerCase("de-AT").trim().replace(/\s+/g, " ")
    const normalizedQuery = normalize(query)
    const normalizedTokens = tokens.map(normalize)
    const wordStartsWith = (value: string, token: string) => value
      .split(/[^\p{L}\p{N}]+/u)
      .some(word => word.startsWith(token))

    const searchableProducts = tempProducts.filter(product => {
      const name = normalize(product.Name || "")
      const description = normalize(String(product.Description || "").replace(/<[^>]*>/g, " "))
      return normalizedTokens.every(token => name.includes(token) || wordStartsWith(description, token))
    })

    const relevance = (product: Product) => {
      const name = normalize(product.Name || "")
      const description = normalize(String(product.Description || "").replace(/<[^>]*>/g, " "))
      if (name === normalizedQuery) return 0
      if (name.startsWith(normalizedQuery)) return 1
      if (normalizedTokens.every(token => wordStartsWith(name, token))) return 2
      if (name.includes(normalizedQuery)) return 3
      if (normalizedTokens.every(token => name.includes(token))) return 4
      if (description.includes(normalizedQuery)) return 5
      return 6
    }

    return searchableProducts.sort((a, b) => {
      const scoreDifference = relevance(a) - relevance(b)
      return scoreDifference || a.Name.localeCompare(b.Name, "de-AT")
    });
  }
  async function searchProductsByName(searchString: string): Promise<Product[]> {
    const tempProducts: Product[] = []
    const params = new URLSearchParams()
    params.set("populate", "Images,categories")
    appendContainsAllTokens(params, "Name", searchString)
    const response = await api.get("products?" + params.toString());
    response.data.data.forEach((value:any) => {
      const tempProduct = new Product();
      tempProduct.parseJson(value);
      tempProducts.push(tempProduct);
    });
    return tempProducts;
  }
  async function login(email: string, password: string): Promise<User> {
    try {
      const response = await api.post("/auth/local", {
        identifier: email,
        password: password,
      });

      const user = new User()
      user.parseJson(response.data.user)
      user.token = response.data.jwt
      return user;
    }
    catch (e:any){
      const error = JSON.parse(e.request.response).error
      if(error.name == "ValidationError"){
        throw "ValidationError"
      }
      else{
        throw "Other Error"
      }
    }
  }

  async function searchUsersByUsername(searchString: string): Promise<User[]> {
    const tempUsers: User[] = []
    const params = new URLSearchParams()
    appendContainsAllTokens(params, "username", searchString)
    const response = await api.get("users?" + params.toString());
    response.data.forEach((value:any) => {
      const tempUser = new User();
      tempUser.parseJson(value);
      tempUsers.push(tempUser);
    });
    return tempUsers;
  }

  async function getUserRole(){
    try {
      const response = await api.get("users/me?populate=*");
      console.log(response.data)
      return response.data.role
    }
    catch (e) {
      console.log(e)
    }
  }
  async function getUserCart(): Promise<Cart>{
    let cart:Cart = new Cart()
    const userDataStore = useUserDataStorage();
    if(!userDataStore.loggedIn){return cart}
    const { user } = storeToRefs(userDataStore)
    try {
      const response = await api.get("users/me?populate=Cart.product,Cart.product.Images");
      cart.parseJSON(response.data.Cart)
      return cart
    }
    catch (e) {
      console.log(e)
    }

    return cart
  }
  async function updateCart(cart:Cart){
    if(!useUserDataStorage().loggedIn){
      return
    }
    const userDataStore = useUserDataStorage();
    const { user } = storeToRefs(userDataStore)
    try {
      const response = await api.put("users/"+user.value?.id,{
            Cart:cart.toApiStruct()
          },
          {
            headers:{
              Authorization: `Bearer ${user.value?.token}`,
            },
          }
      );
    }
    catch (e) {
      console.log(e)
    }
  }
  async function sendRequest(requestData:Ref<RequestData>,cart:Cart){
    try {
      const fromDate = formatRequestDate(requestData.value.date_from)
      const toDate = formatRequestDate(requestData.value.date_to)
      let response = await api.get("sendRequest",{
        params:{
          "name":requestData.value.name,
          "address":requestData.value.address,
          "city":requestData.value.city,
          "company_name":requestData.value.company_name,
          "eventDays":requestData.value.eventDays,
          "description":requestData.value.description,
          "phone":requestData.value.telephone,
          "email":requestData.value.email,
          "fromDate":fromDate,
          "toDate":toDate,
          "cart": JSON.stringify(cartToJson(cart)),
          "subscribeNewsletter":requestData.value.subscribeNewsletter ? requestData.value.subscribeNewsletter : false,
        },
        timeout:5000
      })
      if(response.status === 200){
        return true
      }
      else{
        return false
      }
    }
    catch (e) {
      console.log(e)
      return false
    }
  }

  async function getFeaturedBlogArticles(page:number, pageSize:number,filterString:string) {
    page++
    let url = "blog-articles?populate=Image&pagination[page]="+page+"&pagination[pageSize]="+pageSize+"&sort=publishedAt:desc"
    if(userDataStore.loggedIn) url += "&publicationState=preview"
    return await getBlogArticleRequest(url)
  }
  async function getBlogArticlesOfCategory(page:number, pageSize:number,filterString:string,categoryId:number) {
    page++
    let url = "blog-articles?populate=Image&pagination[page]="+page+"&pagination[pageSize]="+pageSize+"&filters[blog_category][$eq]="+categoryId+"&sort=publishedAt:desc"
    if(userDataStore.loggedIn) url += "&publicationState=preview"
    return await getBlogArticleRequest(url)
  }

  async function getBlogArticleRequest(url:string){
    const response = await api.get(url);
    const totalRecords = response.data.meta.pagination.total
    const blogArticles:BlogArticle[] = response.data.data.map((value:any) => {
      const temp = new BlogArticle();
      temp.parseJson(value);
      return temp;
    });
    return {blogArticles, totalRecords}
  }

  async function getBlogArticleBySlug(slug:string) {
    const tempBlogArticle = new BlogArticle();
    const url = "blog-articles?filters[slug][$eq]="+slug+"&populate=Image,blog_category"

    try {
      const response = await api.get(url);
      if(response.data.data.length > 0){
        tempBlogArticle.parseJson(response.data.data[0]);
      }
    } catch (e) {
      throw(e)
    }
    return tempBlogArticle
  }

  async function getBlogArticle(id:number) {
    const response = await api.get("blog-articles/"+id+"?populate=Image,blog_category");
    const blogArticle = new BlogArticle();
    blogArticle.parseJson(response.data.data)

    return blogArticle
  }

  async function getBlogCategories() {
    const response = await api.get(
        "blog-categories?pagination[pageSize]=100"
    );
    const responseCats = response.data.data as {
      id: number;
      attributes: { [key: string]: any };
    }[];
    let categories: BlogCategory[] = [];
    responseCats.forEach((value) => {
      const tempCategory = new BlogCategory();
      tempCategory.parseJson(value);
      categories.push(tempCategory);
    });

    return categories;
  }

  async function getBlogCategory(id:number) {
    const response = await api.get(
        "blog-categories/"+id
    );

    let category = new BlogCategory();
    category.parseJson(response.data.data)

    return category;
  }

  async function getReferences(){
      const references:Reference[] = []
      const response = await api.get("references?populate=image")
      if(response.data.data){
        response.data.data.forEach((value:any) => {
            const newReference = new Reference()
            newReference.parseJson(value)
            references.push(newReference)
        });
      }
      return references
  }
  async function getReference(id:number){
      const reference:Reference = new Reference()
      const response = await api.get(`references/${id}?populate=image`)

      if(response.data.data){
        reference.parseJson(response.data.data)
      }

      return reference
  }

  async function uploadFile(fileToUpload:File):Promise<{id:number, url:string}>{
    //Get all files
    const files = JSON.parse((await api.get("/upload/files")).request.responseText)

    const retValue = {id:-1, url:""}
    files.forEach((file:any) => {
      if(file.name == fileToUpload.name){
        retValue.id = file.id
        retValue.url = host + file.url
        return
      }
    })
    
    if(retValue.id >= 0){
      alert("Datei existiert bereits")
      return retValue
    }
    else{
      const form = new FormData();
      form.append('files', fileToUpload, fileToUpload.name);
      const response = JSON.parse((await api.post("/upload/",form)).request.response)[0]
      retValue.id = response.id
      retValue.url = host + response.url
      return retValue
    }
  }


  return {baseUrl, api,get,post,put,update,remove,create,downloadEventDocument,generateEventCableList,assignArticleToEvent,clearArticleEvent,assignArticleInternalUse,clearArticleInternalUse,markArticleSold,markArticleInStock,assignArticleToCase,removeArticleFromCase,assignCaseToEvent,clearCaseEvent,assignCaseInternalUse,clearCaseInternalUse,getCasesPaginated,getCase,searchCases,searchArticles,getCategory, getCategoryBySlug,getCategories,getAllCategories,getEventCategories, getCategoryProducts, getProduct,getProductBySlug, getProductsPaginated, getArticlesPaginated, login,searchProducts,getUserRole, getUserCart,
    updateCart,sendRequest,searchProductsByName,getFeaturedBlogArticles,getBlogArticle,getBlogArticleBySlug,getBlogCategories,getBlogArticlesOfCategory,getBlogCategory,searchUsersByUsername, getReferences, getReference, uploadFile,sendFeedbackMail,markFeedbackGiven};
}
